import { useState, useEffect, useRef } from "react";
import { subscribeToBout } from "../firebase/services";
import logo from "../assets/logo.png";
import UnavailableView from "./UnavailableView";
import { QRCodeSVG } from "qrcode.react";
import { POOL_SCORES, ELIM_SCORES, TIEBREAK_CRITERIA } from "../utils/scoring";
import { useDialog } from "../hooks/useDialog";

const BLUE = { bg: "#1565C0", light: "#1E88E5", dark: "#0D47A1", accent: "#64B5F6" };
const RED = { bg: "#D32F2F", light: "#E53935", dark: "#B71C1C", accent: "#EF9A9A" };

const sanitize = (name) => name.replace(/[\.\#\$\/\[\]]/g, "_");

// Live panels need compact, consistently sized labels. Keep a single-word name
// intact; otherwise show the first name and the final surname initial.
const compactJudgeName = (name = "") => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length < 2) return name;
  return `${parts[0]} ${parts[parts.length - 1][0]}.`;
};

// One judge's live panel: touch counters while the round is open. A small status
// tag by their name flips from SCORING to SUBMITTED once they lock in their round,
// but the number shown stays the raw touch count the whole time — it does not switch
// to the judge's translated round score, and it does not reset to 0 either (liveTaps
// itself does reset server-side on submit, to arm the counter for the next round —
// see submitJudgeRound in services.js). Instead this panel remembers the last live
// count it saw before submission and keeps showing that, frozen, until the round
// actually changes. Once the ref confirms the round, this panel is replaced entirely
// by the scoreboard (see isLive below).
function JudgePanel({ judgeName, bout }) {
  const key = sanitize(judgeName);
  const isActive = (bout.activeJudges || []).includes(judgeName);
  const sub = bout.judgeSubmissions?.[key];
  const myRoundsScored = sub?.rounds?.length || 0;
  const openRound = bout.currentRound || 0;
  const hasSubmittedRound = myRoundsScored > openRound;
  const liveTaps = bout.liveTaps?.[key] || { blue: 0, red: 0 };

  const lastLiveRef = useRef({ blue: 0, red: 0 });
  const frozenRef = useRef({ round: null, taps: null });
  if (frozenRef.current.round !== openRound) {
    frozenRef.current = { round: openRound, taps: null };
    lastLiveRef.current = { blue: 0, red: 0 };
  }
  if (!hasSubmittedRound) {
    lastLiveRef.current = liveTaps;
  } else if (frozenRef.current.taps === null) {
    frozenRef.current.taps = lastLiveRef.current;
  }
  const taps = hasSubmittedRound ? frozenRef.current.taps : liveTaps;

  const statusLabel = !isActive ? "NOT JOINED" : hasSubmittedRound ? "SUBMITTED" : "SCORING";
  const statusColor = !isActive ? "#bbb" : hasSubmittedRound ? "#81C784" : "#FFB74D";

  return (
    <div className="flex flex-col overflow-hidden" style={{ minHeight: 0 }}>
      <div className="px-3 py-2 bg-dark-900 shrink-0 border-b border-dark-500/30">
        <div className="flex items-start">
          <div className="flex flex-col min-w-0 flex-1">
            <span className="text-[9px] font-bold text-gray-600 tracking-widest">JUDGE</span>
            <span className="font-display text-sm sm:text-base font-bold text-gray-300 tracking-wide break-words leading-tight">{compactJudgeName(judgeName)}</span>
          </div>
        </div>
        <span className="mt-1 flex items-start justify-end gap-1 text-right text-[9px] font-bold tracking-wider leading-[1.1]" style={{ color: statusColor }}>
          <span className="w-1.5 h-1.5 rounded-full shrink-0 mt-0.5" style={{ background: statusColor }} />
          <span>{statusLabel}</span>
        </span>
      </div>
      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="flex-1 flex flex-col items-center justify-center" style={{ background: `linear-gradient(180deg, ${BLUE.light}, ${BLUE.dark})`, opacity: isActive ? 1 : 0.35 }}>
          <span className="font-display font-extrabold text-white leading-none" style={{ fontSize: "min(11vw, 80px)" }}>
            {isActive ? taps.blue : "–"}
          </span>
          <span className="mt-1 text-[8px] sm:text-[10px] font-bold tracking-widest text-white/60">TOUCHES</span>
        </div>
        <div className="flex-1 flex flex-col items-center justify-center" style={{ background: `linear-gradient(0deg, ${RED.light}, ${RED.dark})`, opacity: isActive ? 1 : 0.35 }}>
          <span className="font-display font-extrabold text-white leading-none" style={{ fontSize: "min(11vw, 80px)" }}>
            {isActive ? taps.red : "–"}
          </span>
          <span className="mt-1 text-[8px] sm:text-[10px] font-bold tracking-widest text-white/60">TOUCHES</span>
        </div>
      </div>
    </div>
  );
}

function judgeGridClass(count) {
  if (count <= 1) return "grid-cols-1";
  if (count === 2) return "grid-cols-2";
  if (count === 3) return "grid-cols-3";
  if (count === 4) return "grid-cols-4";
  if (count === 5) return "grid-cols-5";
  return "grid-cols-6";
}

function UpNextBox({ label, labelColor, bout }) {
  if (!bout) return null;
  return (
    <div className="bg-black bg-opacity-50 rounded-xl text-center px-3 py-2 sm:px-6 sm:py-3 min-w-0">
      <div className="font-bold tracking-widest" style={{ fontSize: "clamp(10px, 2vw, 27px)", color: labelColor }}>{label}</div>
      <div className="font-display font-bold text-gray-300 break-words leading-tight sm:whitespace-nowrap" style={{ fontSize: "clamp(14px, 2.5vw, 36px)" }}>
        <span style={{ color: RED.accent }}>{bout.fighterRed}</span>
        <span className="text-gray-600"> vs </span>
        <span style={{ color: BLUE.accent }}>{bout.fighterBlue}</span>
      </div>
    </div>
  );
}

function SpectatorCodeBadge({ code }) {
  if (!code) return null;
  return (
    <div className="bg-black bg-opacity-50 rounded-lg px-2.5 py-1.5 inline-block">
      <div className="text-[9px] font-bold text-gray-500 tracking-widest">SPECTATOR CODE</div>
      <div className="font-display text-sm font-bold text-gray-300 tracking-[0.2em]">{code}</div>
    </div>
  );
}

function LineupTabButton({ onClick }) {
  return (
    <button onClick={onClick}
      className="bg-black bg-opacity-50 text-gray-400 text-xs px-3 py-1.5 rounded-lg border-none cursor-pointer hover:text-white font-display tracking-wide">
      ☰ LINEUP
    </button>
  );
}

function EventHomeButton({ eventId }) {
  return (
    <button onClick={() => { window.location.href = `/public/${eventId}`; }}
      className="bg-black bg-opacity-50 text-gray-300 text-[10px] sm:text-xs px-2.5 py-1.5 rounded-lg border-none cursor-pointer hover:text-white font-display tracking-wide whitespace-nowrap">
      ← EVENT HOME
    </button>
  );
}

function SpectatorQR({ path }) {
  const url = `${window.location.origin}${path}`;
  return (
    <div className="w-52 bg-white rounded-lg p-2.5 shadow-lg text-center">
      <div className="text-[11px] leading-tight font-bold text-gray-900 mb-1.5">
        To view this event on your device, scan this QR code
      </div>
      <div className="flex justify-center" role="img" aria-label="QR code for this pool's lineup"><QRCodeSVG value={url} size={116} level="M" includeMargin={false} aria-hidden="true" /></div>
      <a href={url} className="block text-[10px] text-blue-800 underline break-all mt-1">{url}</a>
    </div>
  );
}

function ScoringInfoButton({ onClick }) {
  return (
    <button onClick={onClick}
      className="bg-black bg-opacity-50 text-gray-400 text-[10px] sm:text-xs px-2.5 sm:px-3 py-1.5 rounded-lg border-none cursor-pointer hover:text-white font-display tracking-wide whitespace-nowrap">
      HOW DOES SCORING WORK?
    </button>
  );
}

function SpectatorBrand() {
  return (
    <div className="flex flex-col items-center text-center">
      <img src={logo} alt="Joy of HEMA" className="w-full max-w-xs mx-auto" />
      <span className="mt-2 text-gray-400 text-sm font-display font-bold tracking-[0.25em] whitespace-nowrap">
        STUDY • FENCE • GROW
      </span>
    </div>
  );
}

function BoutMeta({ disciplineName, poolName, round, totalRounds, view, className = "" }) {
  const parts = [
    disciplineName,
    poolName,
    round && totalRounds ? `Round ${round} of ${totalRounds}` : null,
    view,
  ].filter(Boolean);

  return <div className={`font-display font-bold tracking-wide ${className}`}>{parts.join(" - ")}</div>;
}

function ScoringMethodology({ onClose }) {
  const dialogRef = useDialog(onClose);
  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-3 sm:p-6" style={{ zIndex: 11000 }} onClick={onClose}>
      <div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="scoring-methodology-title" tabIndex={-1} className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-dark-900 border border-dark-500 rounded-2xl shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 flex items-center justify-between gap-3 px-4 py-3 bg-dark-900 border-b border-dark-500">
          <h2 id="scoring-methodology-title" className="font-display text-lg font-bold text-white">How Scoring Works</h2>
          <button onClick={onClose} aria-label="Close scoring methodology" className="bg-dark-800 border border-dark-500 text-gray-400 px-3 py-1.5 rounded-lg text-xs font-display cursor-pointer hover:text-white">CLOSE</button>
        </div>

        <div className="p-4 sm:p-5 space-y-5 text-sm text-gray-300">
          <section>
            <h3 className="font-display text-xs font-bold text-gray-500 tracking-widest mb-2">WHAT YOU ARE SEEING</h3>
            <p className="leading-relaxed">During a round, each judge independently counts effective hits. The colored live numbers are those running touch counts, not the final awarded score. After the round, each judge evaluates the whole performance and submits a score. The judge determines which fighter was superior in the bout, then assigns a score in line with the scale below:</p>
          </section>

          <section>
            <h3 className="font-display text-xs font-bold text-gray-500 tracking-widest mb-2">ROUND SCORES</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[{ title: "Pool bouts", scores: POOL_SCORES }, { title: "Elimination bouts", scores: ELIM_SCORES }].map((group) => (
                <div key={group.title}>
                  <div className="font-display text-xs font-bold text-gray-400 mb-2">{group.title}:</div>
                  <div className="space-y-2">
                    {group.scores.map((score) => (
                      <div key={score.label} className="flex items-center justify-between gap-3 bg-dark-800 border border-dark-500 rounded-lg px-3 py-2">
                        <span className="font-display text-lg font-bold text-white">{score.label}</span>
                        <span className="text-xs text-gray-400 text-right">{score.desc}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section>
            <h3 className="font-display text-xs font-bold text-gray-500 tracking-widest mb-2">HOW A WINNER IS DECIDED</h3>
            <p className="leading-relaxed">At the end of a bout (whether one round or several), each fighter’s score across all judges is added together. The fighter with the most points is declared winner.</p>
          </section>

          <section>
            <h3 className="font-display text-xs font-bold text-gray-500 tracking-widest mb-2">TIES &amp; TECHNICAL BONUS</h3>
            <p className="leading-relaxed mb-2">Any individual round can end in a tie. If an individual judge’s completed card is tied at the end of a full bout, that judge may confirm the card as a draw or award a +1 technical bonus to one fighter. A bonus requires a brief explanation. Judges weigh:</p>
            <div className="space-y-1.5">
              {TIEBREAK_CRITERIA.map((criterion) => (
                <div key={criterion.title} className="text-xs leading-relaxed">
                  <span className="font-bold text-yellow-600">{criterion.title}:</span>{" "}
                  <span className="text-gray-400">{criterion.title === "Effective strikes" ? "Which fighter had more effective strikes, such as stronger hits." : criterion.desc}</span>
                </div>
              ))}
            </div>
            <p className="mt-3 leading-relaxed">If fighters’ combined scores across all judges are tied, the following will occur:</p>
            <div className="mt-2 space-y-2 text-xs leading-relaxed text-gray-400">
              <p><span className="font-bold text-gray-300">For pool bouts:</span> A pool bout may finish as a draw if the combined total remains tied.</p>
              <p><span className="font-bold text-gray-300">For elimination bouts:</span> An elimination bout cannot finish as a draw; if the combined total remains tied, the referee adds tiebreaker rounds until a winner is decided.</p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

// Full pool bout lineup, opened via the LINEUP tab — read-only schedule/result list
// so spectators can see what's already happened and what's coming up in the pool.
export function SpectateLineupView({ eventId, disciplineName, poolName, bouts, currentBoutId, onWatchLive, onClose }) {
  return (
    <div className="fixed inset-0 bg-black flex flex-col overflow-y-auto select-none" style={{ zIndex: 10000 }}>
      <div className="sm:hidden py-2 px-3 border-b border-dark-500 shrink-0">
        <SpectatorBrand />
        <div className="mt-2 flex items-center justify-between gap-2">
          <EventHomeButton eventId={eventId} />
          {onWatchLive && <button onClick={onWatchLive}
            className="bg-green-action border-none text-white text-xs px-3 py-1.5 rounded-lg cursor-pointer hover:brightness-110 font-display whitespace-nowrap">
            LIVE SCORE
          </button>}
        </div>
        <div className="mt-2 text-center">
          <div className="text-[10px] font-bold text-gray-500 tracking-widest">POOL LINEUP</div>
          <div className="font-display text-lg font-bold text-white break-words">{disciplineName} · {poolName}</div>
        </div>
      </div>
      <div className="hidden sm:flex items-center justify-between px-4 py-4 border-b border-dark-500 shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <EventHomeButton eventId={eventId} />
          <img src={logo} alt="Joy of HEMA" className="h-7 sm:h-9 w-auto shrink-0" />
          <div className="min-w-0">
            <div className="text-[10px] font-bold text-gray-500 tracking-widest">POOL LINEUP</div>
            <div className="font-display text-lg font-bold text-white truncate">{disciplineName} · {poolName}</div>
          </div>
        </div>
        {onWatchLive && <button onClick={onWatchLive}
          className="bg-green-action border-none text-white text-xs px-3 py-1.5 rounded-lg cursor-pointer hover:brightness-110 font-display whitespace-nowrap">
          LIVE SCORE
        </button>}
        <button onClick={onClose}
          className={`${onWatchLive ? "hidden " : ""}bg-dark-800 border border-dark-500 text-gray-400 text-xs px-3 py-1.5 rounded-lg cursor-pointer hover:text-white font-display`}>
          ✕ CLOSE
        </button>
      </div>
      <div className="flex-1 overflow-auto p-3">
        <div className="max-w-xl mx-auto flex flex-col gap-2">
          {bouts.map((b, i) => {
            const isCurrent = b.id === currentBoutId;
            const isDone = b.status === "confirmed";
            const result = b.confirmedResult;
            return (
              <div key={b.id} className="rounded-xl border p-3"
            style={{ background: isCurrent ? "#132a13" : "#1c1c1c", borderColor: isCurrent ? "#81C784" : "#666" }}>
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="font-display text-xs font-bold text-gray-600 shrink-0">#{Number.isInteger(b.order) ? b.order + 1 : i + 1}</span>
                    <span className="font-display text-sm font-bold truncate" style={{ color: RED.accent }}>{b.fighterRed}</span>
                    <span className="text-gray-600 text-xs shrink-0">vs</span>
                    <span className="font-display text-sm font-bold truncate" style={{ color: BLUE.accent }}>{b.fighterBlue}</span>
                  </div>
                  {isCurrent && <span className="text-xs font-bold tracking-wider shrink-0" style={{ color: "#4CAF50" }}>● LIVE</span>}
                  {!isCurrent && !isDone && <span className="text-xs font-bold text-gray-600 tracking-wider shrink-0">UPCOMING</span>}
                  {!isCurrent && isDone && result && (
                    <div className="flex items-center gap-2 text-xs text-gray-600 shrink-0">
                      <span className="font-display font-bold" style={{ color: RED.accent }}>{result.scoreA}</span>
                      <span className="text-gray-700">–</span>
                      <span className="font-display font-bold" style={{ color: BLUE.accent }}>{result.scoreB}</span>
                      {result.winner && result.winner !== "Draw" ? (
                        <span className="font-display font-bold text-xs ml-1" style={{ color: "#4CAF50" }}>Winner: {result.winner}</span>
                      ) : (
                        <span className="font-display font-bold text-xs ml-1 text-gray-500">Draw</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// Shared footer row for both spectate layouts — gives NEXT UP / ON DECK their own
// space (instead of floating over the content) with EXIT pinned to the same row.
function BottomBar({ onExit, nextBout, onDeckBout, qrPath, onScoringInfo }) {
  if (!onExit && !nextBout && !onDeckBout && !onScoringInfo) return null;
  return (
    <div className="flex flex-col gap-2 py-3 px-3 bg-dark-900 border-t border-dark-500 shrink-0 sm:grid sm:items-center" style={{ gridTemplateColumns: "1fr auto 1fr" }}>
      <div className="flex items-center justify-between w-full self-start sm:self-auto">
        {onExit && (
          <button onClick={onExit}
            className="bg-black bg-opacity-50 text-gray-500 text-xs px-3 py-1.5 rounded-lg border-none cursor-pointer hover:text-gray-300 font-display">
            ← EXIT
          </button>
        )}
        <div className="sm:hidden">{onScoringInfo && <ScoringInfoButton onClick={onScoringInfo} />}</div>
      </div>
      <div className="flex flex-col gap-2 w-full sm:flex-row sm:gap-6 sm:w-auto sm:justify-self-center">
        <UpNextBox label="NEXT UP" labelColor="#fff" bout={nextBout} />
        <UpNextBox label="ON DECK" labelColor="#9CA3AF" bout={onDeckBout} />
      </div>
      <div className="hidden sm:flex items-center gap-3 justify-self-end">
        {onScoringInfo && <ScoringInfoButton onClick={onScoringInfo} />}
        {qrPath && <SpectatorQR path={qrPath} />}
      </div>
    </div>
  );
}

export default function SpectateBoutView({ eventId, boutId, onExit, nextBout, onDeckBout, spectatorCode, disciplineName, poolName, poolBouts, defaultLineup = false, onViewChange }) {
  const [bout, setBout] = useState(null);
  const [boutLoaded, setBoutLoaded] = useState(false);
  const [showLineup, setShowLineup] = useState(defaultLineup);
  const [showScoringInfo, setShowScoringInfo] = useState(false);
  const openLineup = () => { setShowLineup(true); onViewChange?.("lineup"); };
  const openLiveScore = () => { setShowLineup(false); onViewChange?.("live"); };

  useEffect(() => {
    const unsub = subscribeToBout(eventId, boutId, (data) => {
      setBout(data);
      setBoutLoaded(true);
    });
    return unsub;
  }, [eventId, boutId]);

  if (!boutLoaded) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-black text-gray-600 font-display text-xl" style={{ zIndex: 9999 }}>
        Loading...
      </div>
    );
  }
  if (!bout) return <UnavailableView eventId={eventId} />;

  const code = spectatorCode || bout.spectateCode;
  const hasLineup = poolBouts && poolBouts.length > 0;
  const qrPath = hasLineup ? `/spectate/pool/${eventId}/${bout.disciplineId}/${encodeURIComponent(poolName)}?view=lineup` : null;
  const judges = bout.judges || [];
  const confirmedRounds = bout.confirmedRounds || [];
  const totalRounds = bout.totalRounds || 1;
  const openRound = bout.currentRound || 0;
  const displayedRound = Math.min(openRound + 1, totalRounds);
  const displayedDiscipline = disciplineName || bout.disciplineName;
  const displayedPool = poolName || bout.poolName;
  const isFinished = bout.status === "confirmed";
  // True once the ref has locked in the score for the round that's currently open —
  // at that point there's a real score to show, so we drop the judge panels and
  // reveal it via the scoreboard below (with no winner declared until it's the
  // last round) instead of leaving spectators on a "waiting" placeholder.
  const roundLockedIn = confirmedRounds.some((r) => r.roundIndex === openRound);
  const isLive = !isFinished && !roundLockedIn && judges.length > 0;

  if (isLive) {
    return (
      <div className="fixed inset-0 flex flex-col overflow-y-auto select-none bg-black" style={{ zIndex: 9999 }}>
        <div className="sm:hidden py-2 px-3 bg-dark-900 border-b border-dark-500 shrink-0">
          <SpectatorBrand />
          <div className="mt-2 flex items-center justify-between gap-2">
            <EventHomeButton eventId={eventId} />
            <div className="flex items-center gap-2">
              {hasLineup && <LineupTabButton onClick={openLineup} />}
            </div>
          </div>
          <div className="mt-2 flex items-center justify-center gap-2 min-w-0 text-center">
            <span className="font-display text-sm font-bold uppercase tracking-wide break-words" style={{ color: RED.accent }}>{bout.fighterRed}</span>
            <span className="text-gray-600 text-xs shrink-0">vs</span>
            <span className="font-display text-sm font-bold uppercase tracking-wide break-words" style={{ color: BLUE.accent }}>{bout.fighterBlue}</span>
          </div>
          <BoutMeta disciplineName={displayedDiscipline} poolName={displayedPool} round={displayedRound} totalRounds={totalRounds}
            view="Live Hit Tracker" className="mt-1 text-center text-[10px] text-gray-500" />
        </div>
        <div className="hidden sm:grid items-center gap-2 py-3 px-3 bg-dark-900 border-b border-dark-500 shrink-0"
          style={{ gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr) minmax(0, 2fr) minmax(0, 1fr) minmax(0, 1fr)" }}>
          <div className="justify-self-start flex items-center gap-2 min-w-0"><EventHomeButton eventId={eventId} /><SpectatorCodeBadge code={code} /></div>
          <div className="justify-self-center"><img src={logo} alt="" className="h-8 md:h-10 w-auto shrink-0" /></div>
          <div className="flex flex-col items-center justify-center min-w-0 text-center">
            <div className="flex items-center gap-3 justify-center min-w-0">
              <span className="font-display text-3xl md:text-5xl font-bold uppercase tracking-wide truncate" style={{ color: RED.accent }}>{bout.fighterRed}</span>
              <span className="text-gray-600 text-base">vs</span>
              <span className="font-display text-3xl md:text-5xl font-bold uppercase tracking-wide truncate" style={{ color: BLUE.accent }}>{bout.fighterBlue}</span>
            </div>
            <BoutMeta disciplineName={displayedDiscipline} poolName={displayedPool} round={displayedRound} totalRounds={totalRounds}
              view="Live Hit Tracker" className="mt-1 text-lg md:text-xl text-gray-400" />
          </div>
          <div className="justify-self-center min-w-0">
            <span className="hidden sm:inline text-white text-sm md:text-lg lg:text-xl font-display font-bold tracking-[0.15em] whitespace-nowrap">
              www.JoyOfHEMA.com
            </span>
          </div>
          <div className="justify-self-end flex items-center gap-2 min-w-0">
            {hasLineup && <LineupTabButton onClick={openLineup} />}
          </div>
        </div>
        <div className={`flex-1 grid ${judgeGridClass(judges.length)}`} style={{ gap: "1px", background: "#000" }}>
          {judges.map((j) => (
            <JudgePanel key={j} judgeName={j} bout={bout} />
          ))}
        </div>
        <BottomBar onExit={onExit} nextBout={nextBout} onDeckBout={onDeckBout} qrPath={qrPath} onScoringInfo={() => setShowScoringInfo(true)} />
        {showLineup && <SpectateLineupView eventId={eventId} disciplineName={disciplineName} poolName={poolName} bouts={poolBouts} currentBoutId={boutId} onWatchLive={openLiveScore} onClose={openLiveScore} />}
        {showScoringInfo && <ScoringMethodology onClose={() => setShowScoringInfo(false)} />}
      </div>
    );
  }

  // ─── SCOREBOARD ───
  // Shown once the open round is locked in (even mid-bout) and once the bout is
  // fully confirmed. Only declares a winner once every round is in — otherwise
  // it's just the running score, no winner banner.
  const totalRed = confirmedRounds.reduce((s, r) => s + (r.red || 0), 0);
  const totalBlue = confirmedRounds.reduce((s, r) => s + (r.blue || 0), 0);
  const currentRoundNum = confirmedRounds.length;
  const allRoundsConfirmed = currentRoundNum >= totalRounds;
  const showResult = isFinished || allRoundsConfirmed;
  const scoreRound = Math.max(1, Math.min(currentRoundNum || displayedRound, totalRounds));
  const scoreView = showResult ? "Final Score" : currentRoundNum > 0 ? `Round ${scoreRound} Score` : "Awaiting Round Score";

  let winnerText = null;
  if (showResult) {
    if (isFinished && bout.confirmedResult) {
      winnerText = bout.confirmedResult.winner && bout.confirmedResult.winner !== "Draw"
        ? `${bout.confirmedResult.winner} WINS`
        : "DRAW";
    } else {
      const provisionalWinner = totalRed > totalBlue ? bout.fighterRed : totalBlue > totalRed ? bout.fighterBlue : null;
      winnerText = provisionalWinner ? `${provisionalWinner} WINS` : "DRAW";
    }
  }

  return (
    <div className="fixed inset-0 flex flex-col overflow-y-auto select-none" style={{ zIndex: 9999 }}>
      <div className="sm:hidden py-2 px-3 bg-dark-900 border-b border-dark-500 shrink-0">
        <SpectatorBrand />
        <div className="mt-2 flex items-center justify-between gap-2">
          <EventHomeButton eventId={eventId} />
          <div className="flex items-center gap-2">
            {hasLineup && <LineupTabButton onClick={openLineup} />}
          </div>
        </div>
      </div>
      <div className="flex-1 flex relative overflow-hidden">
        {/* Red side — LEFT */}
        <div className="flex-1 flex flex-col items-center justify-center relative"
          style={{ background: "linear-gradient(135deg, #D32F2F, #B71C1C)" }}>
          <div className="absolute top-6 left-0 right-0 text-center">
            <div className="font-display text-2xl md:text-5xl font-bold text-white opacity-90 tracking-wide uppercase px-4">
              {bout.fighterRed}
            </div>
            <div className="flex items-center justify-center gap-1 mt-2">
              <div className="w-2.5 h-2.5 rounded-full" style={{ background: "#EF9A9A" }} />
              <span className="text-sm font-semibold tracking-wider" style={{ color: "rgba(255,255,255,0.5)" }}>RED</span>
            </div>
          </div>
          <div className="font-display font-extrabold text-white"
            style={{ fontSize: "min(30vw, 200px)", lineHeight: 1, textShadow: "0 8px 40px rgba(0,0,0,0.3)" }}>
            {totalRed}
          </div>
          <span className="mt-2 text-xs md:text-sm font-bold tracking-widest text-white/60">SCORE</span>
        </div>

        {/* Center divider */}
        <div className="w-1 bg-black relative flex items-center justify-center">
          <div className="absolute bg-black px-3 py-2 rounded-full z-10">
            {showResult ? (
              <span className="font-display text-xs font-bold tracking-widest" style={{ color: "#4CAF50" }}>FINAL</span>
            ) : currentRoundNum > 0 ? (
              <span className="font-display text-xs font-bold tracking-widest text-gray-400">
                R{currentRoundNum}/{totalRounds}
              </span>
            ) : (
              <div className="flex gap-1">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="w-1.5 h-1.5 rounded-full bg-gray-600 pulse-dot" style={{ animationDelay: `${i * 0.2}s` }} />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Blue side — RIGHT */}
        <div className="flex-1 flex flex-col items-center justify-center relative"
          style={{ background: "linear-gradient(135deg, #1565C0, #0D47A1)" }}>
          <div className="absolute top-6 left-0 right-0 text-center">
            <div className="font-display text-2xl md:text-5xl font-bold text-white opacity-90 tracking-wide uppercase px-4">
              {bout.fighterBlue}
            </div>
            <div className="flex items-center justify-center gap-1 mt-2">
              <div className="w-2.5 h-2.5 rounded-full" style={{ background: "#64B5F6" }} />
              <span className="text-sm font-semibold tracking-wider" style={{ color: "rgba(255,255,255,0.5)" }}>BLUE</span>
            </div>
          </div>
          <div className="font-display font-extrabold text-white"
            style={{ fontSize: "min(30vw, 200px)", lineHeight: 1, textShadow: "0 8px 40px rgba(0,0,0,0.3)" }}>
            {totalBlue}
          </div>
          <span className="mt-2 text-xs md:text-sm font-bold tracking-widest text-white/60">SCORE</span>
        </div>

        <div className="absolute top-24 md:top-28 left-3 right-3 z-20 flex justify-center pointer-events-none">
          <BoutMeta disciplineName={displayedDiscipline} poolName={displayedPool} round={scoreRound} totalRounds={totalRounds}
            view={scoreView} className="max-w-full text-center text-[10px] sm:text-lg md:text-xl text-white/80 bg-black/60 px-3 py-2 rounded-lg" />
        </div>

        {winnerText && (
          <div className="absolute bottom-8 left-0 right-0 text-center">
            <div className="inline-block bg-black bg-opacity-80 px-6 py-3 rounded-2xl">
              <span className="font-display text-lg md:text-2xl font-bold tracking-wide" style={{ color: "#4CAF50" }}>
                {winnerText}
              </span>
            </div>
          </div>
        )}

        <div className="hidden sm:flex absolute top-3 left-3 items-center gap-2"><EventHomeButton eventId={eventId} /><SpectatorCodeBadge code={code} /></div>
        <div className="hidden sm:flex absolute top-3 right-3 items-center gap-2">
          {hasLineup && <LineupTabButton onClick={openLineup} />}
        </div>
      </div>
      <BottomBar onExit={onExit} nextBout={nextBout} onDeckBout={onDeckBout} qrPath={qrPath} onScoringInfo={() => setShowScoringInfo(true)} />
      {showLineup && <SpectateLineupView eventId={eventId} disciplineName={disciplineName} poolName={poolName} bouts={poolBouts} currentBoutId={boutId} onWatchLive={openLiveScore} onClose={openLiveScore} />}
      {showScoringInfo && <ScoringMethodology onClose={() => setShowScoringInfo(false)} />}
    </div>
  );
}
